/**
 * AI configuration banner.
 *
 * Surfaces the backend AI subsystem state so users know WHY no insights are
 * showing up. The backend AI generation path silently falls back to
 * rule-based output when the upstream is misconfigured or unreachable,
 * which used to leave users debugging by reading container logs.
 *
 * Customer-facing strings here MUST stay vendor-neutral — never name the
 * underlying provider or model. Ops can see the full reason in backend
 * logs and on the /ai/health admin endpoint.
 *
 * Mount once at the top of the AI feature surface (AIAssistant page).
 * Calls /api/v1/ai/health which is cached server-side for 60s.
 */
import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react'

import { aiHealthService, type AIHealth } from '../../services/aiHealth'

export function AIHealthBanner() {
  const [health, setHealth] = useState<AIHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true)
    else setLoading(true)
    try {
      const res = await aiHealthService.get(refresh)
      setHealth(res)
    } catch {
      // If /ai/health itself 401/403s we don't render anything — the user
      // either isn't logged in or isn't on a plan with AI access.
      setHealth(null)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    load(false)
  }, [])

  if (loading || !health) return null

  // All-good — render a small confirmation strip rather than nothing, so
  // a working setup feels intentional.
  if (health.configured && health.reachable) {
    return (
      <div className="mb-4 flex items-center gap-2 text-xs text-[#486581] bg-green-50 border border-green-200 rounded px-3 py-2">
        <CheckCircle className="w-4 h-4 text-green-600" />
        <span>AI assistant is online.</span>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="ml-auto text-[#486581] hover:text-[#102a43] disabled:opacity-50"
          title="Re-test connection"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`}
          />
        </button>
      </div>
    )
  }

  // Two flavors of "broken": not configured vs configured-but-failing.
  // Body copy is intentionally provider-agnostic — never name the upstream
  // service or expose env var names to end users. Ops can find the full
  // reason in backend logs.
  const title = !health.configured
    ? 'AI assistant is not configured'
    : 'AI assistant is temporarily unavailable'
  const body = !health.configured
    ? 'An administrator needs to finish setting up the AI assistant. In the meantime, insights will use rule-based output.'
    : 'The AI service is not responding. Insights will fall back to rule-based output until this resolves. If the issue persists, contact your administrator.'

  return (
    <div className="mb-4 bg-amber-50 border border-amber-300 rounded p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-amber-900">{title}</h3>
          <p className="text-sm text-amber-800 mt-1 break-words">{body}</p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="text-xs px-3 py-1.5 bg-white border border-amber-300 rounded hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Re-test
        </button>
      </div>
    </div>
  )
}
